import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Title } from '@angular/platform-browser';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ProductService } from 'src/app/service/product.service';
import { ToastrNotificationService } from 'src/app/service/toastr-notification.service';
import { RegisteredProductDetailsDialogComponent } from './registered-product-details-dialog.component';

@Component({
  selector: 'app-registered-products',
  templateUrl: './registered-products.component.html',
  styleUrls: ['./registered-products.component.scss'],
})
export class RegisteredProductsComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = [
    'productCode',
    'product',
    'registeredDate',
    'user',
    'location',
    'action',
  ];

  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  spinner: boolean = true;
  allRegisteredProducts: any[] = [];
  searchTerm: string = '';
  totalRecords: number = 0;
  pageSize: number = 10;
  pageIndex: number = 0;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  constructor(
    private productService: ProductService,
    private titleService: Title,
    private toastr: ToastrNotificationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle('Registered Products');

    // Debounced server-side search across all records
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe((term) => {
        this.searchTerm = term.trim();
        this.pageIndex = 0;
        this.loadRegisteredProducts();
      });

    this.loadRegisteredProducts();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  loadRegisteredProducts(): void {
    this.spinner = true;
    const page = this.pageIndex + 1;

    this.productService
      .getAllRegisteredProducts(this.searchTerm, page, this.pageSize)
      .subscribe({
        next: (response) => {
          this.spinner = false;
          if (response && response.success) {
            this.allRegisteredProducts = response.registeredProducts || [];
            this.totalRecords = response.total !== undefined ? response.total : this.allRegisteredProducts.length;
          } else {
            this.allRegisteredProducts = [];
            this.totalRecords = 0;
          }
          this.dataSource.data = this.allRegisteredProducts;
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }
        },
        error: (error) => {
          this.spinner = false;
          this.toastr.error(
            error.error?.error || error.message || 'Could not load Registered Products',
            'Error'
          );
        },
      });
  }

  onSearch(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchSubject.next(filterValue);
  }

  clearSearch(input: HTMLInputElement): void {
    input.value = '';
    this.searchTerm = '';
    this.pageIndex = 0;
    this.loadRegisteredProducts();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadRegisteredProducts();
  }

  viewDetails(element: any): void {
    this.dialog.open(RegisteredProductDetailsDialogComponent, {
      width: '750px',
      maxWidth: '95vw',
      panelClass: 'registered-details-dialog-panel',
      data: element,
    });
  }

  exportToCsv(): void {
    this.toastr.info('Preparing export...', 'Please wait');
    this.productService
      .getAllRegisteredProducts(this.searchTerm, 1, 2000)
      .subscribe({
        next: (response) => {
          const exportData = (response && response.success) ? (response.registeredProducts || []) : [];
          if (exportData.length === 0) {
            this.toastr.warning('No records available to export.', 'Warning');
            return;
          }

          const headers = [
            'Serial Number',
            'Barcode / QR Code',
            'Product Name',
            'Product Code (SKU)',
            'Category',
            'Registered Date',
            'Customer Name',
            'Customer Phone',
            'Company Name',
            'City',
            'State',
            'Pincode',
            'Warranty Status',
            'Claim Status'
          ];

          const rows = exportData.map((item: any) => [
            item.serialNumber || '',
            item.productCode || '',
            `"${(item.productName || '').replace(/"/g, '""')}"`,
            item.productSKU || '',
            item.categoryName || '',
            item.registeredDate ? new Date(item.registeredDate).toLocaleString() : '',
            `"${(item.user?.username || '').replace(/"/g, '""')}"`,
            item.user?.phone || '',
            `"${(item.user?.companyName || '').replace(/"/g, '""')}"`,
            `"${(item.user?.city || '').replace(/"/g, '""')}"`,
            `"${(item.user?.state || '').replace(/"/g, '""')}"`,
            item.user?.pincode || '',
            item.isCurrentlyEligible ? 'In Warranty' : 'Expired',
            item.isClaimed ? 'Claimed' : 'Unclaimed'
          ]);

          const csvContent = [headers.join(','), ...rows.map((r: any[]) => r.join(','))].join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `Registered_Products_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.toastr.success(`Exported ${exportData.length} records successfully.`, 'Success');
        },
        error: () => {
          this.toastr.error('Failed to export records.', 'Error');
        }
      });
  }
}
